'use client'

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CircleCheck, CircleX, Info, TriangleAlert, X } from "lucide-react";
import Bus, { NotificationPayload } from "@/lib/bus";

type Cat = "standard" | "info" | "success" | "warning" | "error";

interface ToastItem {
    id: string;
    title?: string | null;
    message: string | null;
    category: Cat;
    duration: number;
}

const MAX_TOASTS = 3;

function createToastId() {
    return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function ToastHost() {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const removeToast = useCallback((id: string) => {
        const timer = timersRef.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(id);
        }
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const handleNotification = useCallback((payload: NotificationPayload) => {
        const item: ToastItem = {
            id: createToastId(),
            title: payload.title ?? null,
            message: payload.message,
            category: payload.categoryName,
            duration: payload.duration,
        };

        setToasts((prev) => {
            const next = [...prev];
            if (next.length >= MAX_TOASTS) {
                const oldest = next[0];
                const oldestTimer = timersRef.current.get(oldest.id);
                if (oldestTimer) {
                    clearTimeout(oldestTimer);
                    timersRef.current.delete(oldest.id);
                }
                next.shift();
            }
            next.push(item);
            return next;
        });

        const timer = setTimeout(() => removeToast(item.id), item.duration);
        timersRef.current.set(item.id, timer);
    }, [removeToast]);

    useEffect(() => {
        Bus.on("notification", handleNotification);
        return () => {
            Bus.off("notification", handleNotification);
            timersRef.current.forEach((timer) => clearTimeout(timer));
            timersRef.current.clear();
        };
    }, [handleNotification]);

    return (
        <div className="fixed top-0 z-999 left-1/2 transform -translate-x-1/2 p-4 pointer-events-none">
            <ul className="flex flex-col gap-3">
                <AnimatePresence initial={false}>
                    {toasts.map((toast) => (
                        <motion.li
                            key={toast.id}
                            layout
                            className="pointer-events-auto"
                            initial={{ y: -24, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -24, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 420, damping: 28, mass: 0.6 }}
                        >
                            <div
                                className={`select-none text-card-foreground bg-card/20 gap-2 backdrop-blur-xl shadow-lg pr-7 p-4 border rounded-lg overflow-hidden items-start flex w-[24rem] max-w-105 relative
                                    ${
                                        toast.category === "info"
                                            ? "border-info-notification"
                                            : toast.category === "success"
                                                ? "border-success-border"
                                                : toast.category === "warning"
                                                    ? "border-warning-border"
                                                    : toast.category === "error"
                                                        ? "border-destructive-border"
                                                        : "border-border"
                                    }`}
                            >
                                {toast.category !== "standard" && (
                                    <div className="shrink-0 mt-0.5">
                                        {toast.category === "info" ? (
                                            <Info className="w-5 h-5 text-info-notification" />
                                        ) : toast.category === "warning" ? (
                                            <TriangleAlert className="w-5 h-5 text-warning-notification" />
                                        ) : toast.category === "error" ? (
                                            <CircleX className="w-5 h-5 text-error-notification" />
                                        ) : (
                                            <CircleCheck className="w-5 h-5 text-success-notification" />
                                        )}
                                    </div>
                                )}

                                <div className="flex gap-1.5 items-start flex-col">
                                    {toast.title && <span className="font-semibold text-sm leading-none mt-0.5">{toast.title}</span>}
                                    {toast.message && <span className="text-sm opacity-90">{toast.message}</span>}
                                </div>

                                <button
                                    className="p-1 rounded-md absolute top-1 right-1 cursor-pointer opacity-70 hover:opacity-100 transition-opacity"
                                    onClick={() => removeToast(toast.id)}
                                    aria-label="Toast schließen"
                                >
                                    <X className="text-muted-foreground hover:text-primary transition-colors h-4 w-4" />
                                </button>
                            </div>
                        </motion.li>
                    ))}
                </AnimatePresence>
            </ul>
        </div>
    );
}

